import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { matchesAll } from "@/lib/trFold";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Loader2, Search, Copy, ArrowRight, FileText, ShoppingCart, Trash2,
  Sparkles, Zap, FileDown, Download, Factory, Pencil, Wallet, Send, XCircle, Ban, Info,
  ListChecks, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import ProductionPlanDialog from "@/components/ProductionPlanDialog";
import EditOrderSheet from "@/components/EditOrderSheet";
import PaymentDialog from "@/components/PaymentDialog";
import DuplicateOrderDialog from "@/components/DuplicateOrderDialog";
import QuoteLineApprovalDialog from "@/components/QuoteLineApprovalDialog";
import InvoiceOrderDialog from "@/components/InvoiceOrderDialog";
import { useAuth } from "@/context/AuthContext";
import useViewMode from "@/hooks/useViewMode";
import ViewToggle from "@/components/ViewToggle";

// Teklif sınıfı kalem bazında: sonuçlanmamış (dönüşmemiş + reddedilmemiş) kalem varsa AKTİF.
// Onaylı ama henüz siparişe dönüştürülmemiş kalem "dönüşüm bekliyor" sayılır → aktif.
const QUOTE_FILTERS = [["all", "Tümü"], ["aktif", "Aktif Teklifler"], ["donusen", "Siparişe Dönüşen"], ["reddedilen", "Reddedilen"], ["arsiv", "Arşiv"]];
const quoteClass = (o) => {
  if (o.stage === "iptal") return "arsiv";
  if (o.stage === "reddedildi") return "reddedilen";
  if (o.stage !== "teklif") return o.was_quote ? "donusen" : null;
  const ls = o.line_items || [];
  if (!ls.length) return "aktif";
  if (ls.some((l) => !l.converted_order_id && l.approval_status !== "reddedildi")) return "aktif";
  return ls.some((l) => l.converted_order_id) ? "donusen" : "reddedilen";
};

/**
 * Shared list view used by both /satislar (mode="sales") and /teklifler
 * (mode="quotes"). Renders a table of sales_orders filtered by stage(s).
 */
export default function OrdersList({ mode = "sales" }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isQuotes = mode === "quotes";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [priority, setPriority] = useState("all");
  const [stage, setStage] = useState(searchParams.get("stage") || "all");
  const [preset, setPreset] = useState(searchParams.get("filter") || "");  // in_production | delayed
  const [stagesMeta, setStagesMeta] = useState({ stages: [], labels: {} });
  const [quickOpen, setQuickOpen] = useState(false);
  const [productionOrder, setProductionOrder] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [dupOrder, setDupOrder] = useState(null);
  const [approvalOrder, setApprovalOrder] = useState(null);
  const [invOrder, setInvOrder] = useState(null);
  const [datesOrder, setDatesOrder] = useState(null); // FT fatura tarih düzenleme
  const { can } = useAuth();
  // İrsaliye & Fatura aşamasında sevk kaydı atlanmış olabilir — faturalanabilirlik
  // kuralı backend ile aynı: max(sevk, adet) > faturalanan
  const invoiceable = (o) => !isQuotes && o.stage === "irsaliye_fatura" && can("finance", "add")
    && Math.max(Number(o.shipped_total || 0), Number(o.quantity || 0)) > Number(o.invoiced_total || 0);
  const [view, setView] = useViewMode(mode === "quotes" ? "quotes" : "orders", "list");

  // Sync URL query params → state (browser back/forward + Dashboard entry)
  useEffect(() => {
    const s = searchParams.get("stage") || (isQuotes ? "aktif" : "all");
    const f = searchParams.get("filter") || "";
    setStage(s);
    setPreset(f);
  }, [searchParams]);

  const clearPreset = () => {
    setPreset("");
    setStage(isQuotes ? "aktif" : "all");
    setPriority("all");
    setSearchParams({});
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders");
      setOrders(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/meta/stages").then((r) => setStagesMeta(r.data || { stages: [], labels: {} })).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const base = isQuotes
      ? orders.filter((o) => { const c = quoteClass(o); return c && (stage === "all" || stage === c); })
      : orders.filter((o) => o.stage !== "teklif" && o.stage !== "reddedildi");

    // Preset filters (from Dashboard cards or URL).
    // Aligned with backend /dashboard/summary logic.
    const IN_PRODUCTION_STAGES = [
      "kesim", "baski", "nakis", "surec_kalite", "dikim", "utu_finisaj", "son_kalite",
    ];
    const now = new Date();
    const isDelayed = (o) => {
      if (o.stage === "tamamlandi" || o.stage === "iptal" || o.stage === "reddedildi") return false;
      const dueRaw = o.due_date || o.delivery_date;
      if (!dueRaw) return false;
      const dt = new Date(dueRaw);
      return !isNaN(dt.getTime()) && dt < now;
    };

    return base.filter((o) => {
      if (preset === "in_production" && !IN_PRODUCTION_STAGES.includes(o.stage)) return false;
      if (preset === "delayed" && !isDelayed(o)) return false;
      if (priority !== "all" && o.priority !== priority) return false;
      if (!isQuotes && stage !== "all" && o.stage !== stage) return false;
      if (!q.trim()) return true;
      return matchesAll(q, [o.order_no, o.customer_name, o.product_name]);
    });
  }, [orders, isQuotes, q, priority, stage, preset]);

  const totals = useMemo(() => {
    // FT fatura belgeleri (order_source_id) listede görünür ama toplamlara ikinci kez GİRMEZ
    const sales = filtered.filter((o) => !o.order_source_id);
    const qty = sales.reduce((s, o) => s + (o.quantity || 0), 0);
    const rev = sales.reduce((s, o) => s + (o.total_price || 0), 0);
    return { count: sales.length, qty, rev };
  }, [filtered]);

  const duplicate = async (o) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/duplicate`);
      toast.success(`Kopyalandı → ${data.order_no}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`${o.order_no} silinecek. Devam?`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success("Silindi");
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const promoteToSale = async (o) => {
    try {
      const la = o.line_approval;
      const started = la && (la.approved + la.rejected) > 0;
      if (started && la.approved === 0) {
        // Onay süreci başlamış ama onaylı kalem yok — kalem onay dialog'unu aç
        setApprovalOrder(o);
        toast.info("Önce onaylanacak kalemleri seçin");
        return;
      }
      if (started) {
        const { data } = await api.post(`/orders/${o.id}/convert-approved`);
        if (data.mode === "full") {
          toast.success("Sipariş onaylandı");
        } else {
          toast.success(`${data.converted} kalem siparişe dönüştürüldü (${data.order_no}) · ${data.remaining} kalem teklifte kaldı`);
        }
      } else {
        await api.patch(`/orders/${o.id}/stage`, { stage: "siparis_onay" });
        toast.success("Sipariş onaylandı");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // PDF — popup engelleyicisine takılmamak için window.open YERİNE
  // blob URL + gerçek anchor tıklaması (async sonrası window.open engellenir).
  const createPurchaseRequest = async (o) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/purchase-request`);
      toast.success(`Satın alma talebi oluşturuldu (${data.request_no}) — ${data.items.length} eksik kalem`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) toast.info(String(detail));
      else if (e?.response?.status === 400) toast.warning(String(detail));
      else toast.error(formatApiError(e));
    }
  };

  const openPdf = async (o) => {
    try {
      const resp = await api.get(`/orders/${o.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Teklif sonuçlandırma (iptal / reddedildi) — sebep zorunlu
  const [closeOrder, setCloseOrder] = useState(null); // {o, stage: "iptal"|"reddedildi"}
  const [infoOrder, setInfoOrder] = useState(null);
  const openCloseDialog = (o, targetStage) => {
    setCloseOrder({ o, stage: targetStage });
  };

  const sendToBH = async (o) => {
    if (o.bh_send_status === "unverified" && !window.confirm("Önceki gönderimin sonucu doğrulanamadı. Bizim Hesap'ta bu faturanın OLMADIĞINI kontrol ettiyseniz tekrar gönderin. Devam?")) return;
    try {
      const { data } = await api.post(`/orders/${o.id}/send-bizim-hesap${o.bh_send_status === "unverified" ? "?force=true" : ""}`);
      toast.success(`Bizim Hesap'a gönderildi${data.bh_ref ? ` (ref: ${data.bh_ref})` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return "—"; }
  };

  const PageIcon = isQuotes ? FileText : ShoppingCart;

  return (
    <div>
      <PageHeader
        title={isQuotes ? "Teklifler" : "Satışlar"}
        subtitle={
          `${totals.count} kayıt · ${totals.qty.toLocaleString("tr-TR")} adet · ₺${fmt(totals.rev)}`
        }
        breadcrumbs={["Modüller", isQuotes ? "Teklifler" : "Satışlar"]}
        actions={
          <div className="flex gap-2">
            <Button
              data-testid="orders-export-xlsx-btn"
              onClick={async () => {
                try {
                  const resp = await api.get(`/orders/export/xlsx?mode=${isQuotes ? "quotes" : "sales"}`, { responseType: "blob" });
                  const url = URL.createObjectURL(resp.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `fucan_${isQuotes ? "teklifler" : "satislar"}_${Date.now()}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success("Excel dosyası indiriliyor");
                } catch (err) { toast.error(formatApiError(err)); }
              }}
              variant="outline"
              className="border-emerald-400 dark:border-emerald-800 bg-emerald-100 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-950/60 hover:text-emerald-900 dark:hover:text-emerald-200 disabled:opacity-60 rounded-sm uppercase tracking-wider text-xs font-semibold"
              size="sm"
            >
              <FileDown className="w-4 h-4 mr-1.5" /> Excel
            </Button>
            {(isQuotes ? (can("wizard", "add") || can("quotes", "add")) : can("orders", "add")) && (
            <Button
              data-testid="orders-quick-btn"
              onClick={() => isQuotes ? navigate("/siparis-sihirbazi?mode=quote") : setQuickOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
              size="sm"
            >
              <Zap className="w-4 h-4 mr-1.5" />
              Hızlı {isQuotes ? "Teklif" : "Sipariş"}
            </Button>
            )}
            <Button
              data-testid="orders-wizard-btn"
              onClick={() => navigate("/siparis-sihirbazi")}
              variant="outline"
              className="border-blue-400 dark:border-blue-800 bg-blue-100 dark:bg-blue-950/30 text-blue-900 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-950/60 hover:text-blue-900 dark:hover:text-blue-200 disabled:opacity-60 rounded-sm uppercase tracking-wider text-xs font-semibold"
              size="sm"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              Sihirbaz
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        {/* Preset indicator (from Dashboard cards) */}
        {(preset || stage !== "all") && !isQuotes && (
          <div
            data-testid="orders-preset-indicator"
            className="flex items-center gap-2 px-3 py-2 rounded-sm bg-blue-950/30 border border-blue-900/60 text-xs"
          >
            <span className="text-blue-300 font-mono uppercase tracking-wider">Aktif filtre:</span>
            {preset === "in_production" && (
              <span className="text-blue-100 font-semibold">Üretim aşamasındaki siparişler</span>
            )}
            {preset === "delayed" && (
              <span className="text-red-300 font-semibold">Geciken siparişler</span>
            )}
            {stage !== "all" && !preset && (
              <span className="text-blue-100 font-semibold">
                Aşama: {stagesMeta.labels?.[stage] || stage}
              </span>
            )}
            <Button
              data-testid="orders-preset-clear"
              size="sm"
              variant="ghost"
              onClick={clearPreset}
              className="ml-auto h-6 px-2 text-blue-300 hover:bg-blue-950/60 rounded-sm text-[11px]"
            >
              Temizle
            </Button>
          </div>
        )}

        {/* Filter bar */}
        <Card className="bg-slate-900 border-slate-800 rounded-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="orders-search"
                placeholder="Sipariş no, müşteri veya ürün ara…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="bg-slate-950 border-slate-800 pl-9 rounded-sm text-slate-100"
              />
            </div>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="orders-priority-filter" className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm">
                <SelectValue placeholder="Öncelik" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">Tüm öncelikler</SelectItem>
                <SelectItem value="acil">Acil</SelectItem>
                <SelectItem value="yuksek">Yüksek</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="dusuk">Düşük</SelectItem>
              </SelectContent>
            </Select>
            {isQuotes ? (
              <Select value={QUOTE_FILTERS.some(([v]) => v === stage) ? stage : "aktif"} onValueChange={(v) => setStage(v)}>
                <SelectTrigger data-testid="quotes-archive-filter" className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {QUOTE_FILTERS.map(([v, l]) => (
                    <SelectItem key={v} value={v} data-testid={`quotes-filter-${v}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger data-testid="orders-stage-filter" className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm">
                  <SelectValue placeholder="Aşama" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 max-h-72">
                  <SelectItem value="all">Tüm aşamalar</SelectItem>
                  {(stagesMeta.stages || []).filter((s) => s !== "teklif").map((s) => (
                    <SelectItem key={s} value={s}>{stagesMeta.labels?.[s] || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800">
            <div className="text-[11px] text-slate-500 font-mono" data-testid="orders-count">
              {loading ? "" : `${filtered.length} kayıt`}
            </div>
            <ViewToggle value={view} onChange={setView} testId="orders-view" />
          </div>
        </Card>

        {/* Table / Cards */}
        <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 px-6">
              <PageIcon className="w-10 h-10 mx-auto text-slate-700 mb-3" />
              <div className="text-slate-400 text-sm">
                {isQuotes ? "Bekleyen teklif yok." : "Kayıtlı satış bulunamadı."}
              </div>
            </div>
          ) : view === "grid" ? (
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="orders-cards">
              {filtered.map((o) => (
                <div
                  key={o.id}
                  data-testid={`order-card-${o.id}`}
                  onClick={() => navigate(isQuotes && !o.was_quote ? `/teklif/${o.id}` : `/satis/${o.id}`)}
                  className="group relative rounded-sm border border-slate-800 bg-slate-950/40 p-3 hover:bg-slate-900/60 hover:border-slate-700 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-mono text-sm text-blue-400 font-bold">{o.order_no}</div>
                    <PriorityBadge p={o.priority} />
                  </div>
                  <div className="text-base font-bold text-white truncate leading-tight" data-testid={`order-card-customer-${o.id}`}>{o.customer_name}</div>
                  <div className="text-[10px] text-slate-500 truncate mb-2" data-testid={`order-card-product-${o.id}`}>{o.product_name}</div>
                  <div className="grid grid-cols-2 gap-1 text-[11px] mb-2">
                    <div className="text-slate-500 font-mono">Adet</div>
                    <div className="text-right text-slate-100 font-mono tabular-nums">{o.quantity}</div>
                    <div className="text-slate-500 font-mono">Tutar</div>
                    <div className="text-right text-slate-50 font-mono tabular-nums">{fmt(o.total_price)}</div>
                    <div className="text-slate-500 font-mono">Termin</div>
                    <div className="text-right text-slate-300 font-mono">{fmtDate(o.due_date)}</div>
                    {isQuotes && (
                      <>
                        <div className="text-slate-500 font-mono">Geçerlilik</div>
                        <div className={`text-right font-mono ${
                          o.expires_at && new Date(o.expires_at) < new Date()
                            ? "text-red-400 font-bold" : "text-slate-300"
                        }`}>{fmtDate(o.expires_at)}</div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                    <div className="flex items-center gap-1 flex-wrap">
                      <Badge variant="outline" className="bg-slate-950 border-slate-700 text-slate-300 uppercase text-[9px] font-mono">
                        {o.stage}
                      </Badge>
                      {isQuotes && <LineApprovalBadges o={o} />}
                    </div>

                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-slate-800">
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">No</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Müşteri</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün</TableHead>
                    <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Adet</TableHead>
                    <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tutar ₺</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Termin</TableHead>
                    {isQuotes && (
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Geçerlilik</TableHead>
                    )}
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Aşama</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Öncelik</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((o) => (
                    <TableRow
                      key={o.id}
                      data-testid={`order-row-${o.id}`}
                      onClick={() => navigate(isQuotes && !o.was_quote ? `/teklif/${o.id}` : `/satis/${o.id}`)}
                      className="border-slate-800 hover:bg-slate-800/40 cursor-pointer"
                    >
                      <TableCell className="font-mono text-xs text-blue-500 font-semibold underline-offset-2 hover:underline">{o.order_no}</TableCell>
                      <TableCell className="text-slate-100 text-sm">{o.customer_name}</TableCell>
                      <TableCell className="text-slate-300 text-sm">{o.product_name}</TableCell>
                      <TableCell className="text-right font-mono text-slate-100 tabular-nums">{o.quantity}</TableCell>
                      <TableCell className="text-right font-mono text-slate-50 tabular-nums">{fmt(o.total_price)}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-400">{fmtDate(o.due_date)}</TableCell>
                      {isQuotes && (
                        <TableCell className={`font-mono text-xs ${
                          o.expires_at && new Date(o.expires_at) < new Date()
                            ? "text-red-400 font-bold" : "text-slate-400"
                        }`}>
                          {fmtDate(o.expires_at)}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant="outline" className="bg-slate-950 border-slate-700 text-slate-300 uppercase text-[10px] font-mono">
                          {stagesMeta.labels?.[o.stage] || o.stage}
                        </Badge>
                        {isQuotes && (
                          <span className="inline-flex items-center gap-1 ml-1 align-middle">
                            <LineApprovalBadges o={o} />
                          </span>
                        )}
                        {isQuotes && ["iptal", "reddedildi"].includes(o.stage) && (
                          <div className="text-[10px] text-slate-500 mt-1 max-w-[180px]" data-testid={`quote-close-summary-${o.id}`}>
                            {o.cancel_reason || "Sebep belirtilmemiş"}{o.cancel_note ? ` — ${o.cancel_note}` : ""}
                          </div>
                        )}
                        {o.stage === "fatura" && ["sent", "failed", "unverified"].includes(o.bh_send_status) && (
                          <Badge
                            variant="outline"
                            data-testid={`bh-status-${o.id}`}
                            title={o.bh_send_status === "sent" ? `Bizim Hesap ref: ${o.bh_ref || "-"}` : `Gönderim hatası: ${o.bh_error || "-"}`}
                            className={`ml-1 uppercase text-[10px] font-mono ${o.bh_send_status === "sent"
                              ? "bg-emerald-950/30 border-emerald-900/60 text-emerald-300"
                              : o.bh_send_status === "unverified" ? "bg-amber-950/30 border-amber-900/60 text-amber-300"
                              : "bg-rose-950/30 border-rose-900/60 text-rose-300"}`}
                          >
                            {o.bh_send_status === "sent" ? "BH ✓" : o.bh_send_status === "unverified" ? "BH ?" : "BH Hata"}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <PriorityBadge p={o.priority} />
                      </TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      <QuickOrderDialog
        open={quickOpen}
        onOpenChange={setQuickOpen}
        isQuotes={isQuotes}
        onSaved={load}
      />
      <QuoteLineApprovalDialog
        order={approvalOrder}
        open={!!approvalOrder}
        onOpenChange={(v) => !v && setApprovalOrder(null)}
        onChanged={load}
      />
      <InvoiceOrderDialog
        order={invOrder}
        open={!!invOrder}
        onOpenChange={(v) => !v && setInvOrder(null)}
        onSaved={load}
      />
      <InvoiceDatesDialog
        order={datesOrder}
        onClose={() => setDatesOrder(null)}
        onSaved={load}
      />
      <ProductionPlanDialog
        order={productionOrder}
        open={!!productionOrder}
        onOpenChange={(v) => !v && setProductionOrder(null)}
        onDone={load}
      />
      <EditOrderSheet
        order={editOrder}
        open={!!editOrder}
        onOpenChange={(v) => !v && setEditOrder(null)}
        onSaved={load}
      />
      <PaymentDialog
        order={paymentOrder}
        open={!!paymentOrder}
        onOpenChange={(v) => !v && setPaymentOrder(null)}
        onSaved={load}
      />

      {/* Teklif sonuçlandırma + arşiv bilgisi — export'lu bileşenler (detay sayfasıyla paylaşılır) */}
      <QuoteCloseDialog
        order={closeOrder?.o}
        stage={closeOrder?.stage}
        onClose={() => setCloseOrder(null)}
        onSaved={load}
      />
      <QuoteCloseInfoDialog
        order={infoOrder}
        labels={stagesMeta.labels}
        onClose={() => setInfoOrder(null)}
      />
      <DuplicateOrderDialog
        order={dupOrder}
        open={!!dupOrder}
        onOpenChange={(v) => !v && setDupOrder(null)}
        onDuplicated={load}
      />
    </div>
  );
}

function InfoRow({ label, value, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-800/60 pb-1.5">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono shrink-0">{label}</span>
      <span className="text-slate-200 text-right" data-testid={testid}>{value}</span>
    </div>
  );
}


function PriorityBadge({ p }) {
  const map = {
    acil: "bg-red-950 border-red-800 text-red-300",
    yuksek: "bg-blue-950 border-blue-800 text-blue-300",
    normal: "bg-slate-950 border-slate-700 text-slate-300",
    dusuk: "bg-slate-950 border-slate-800 text-slate-500",
  };
  return (
    <Badge variant="outline" className={`${map[p] || map.normal} uppercase text-[10px] font-mono`}>
      {p}
    </Badge>
  );
}

// Teklif kalem onayı rozetleri: kısmi/tam onay özeti + kısmen dönüştürüldü işareti
function LineApprovalBadges({ o }) {
  const la = o.line_approval;
  const partially = (o.line_items || []).some((l) => l.converted_order_id);
  const started = la && (la.approved + la.rejected) > 0;
  const pending = la && la.pending > 0;
  if (!started && !partially && !pending) return null;
  return (
    <>
      {pending && (
        <Badge variant="outline" data-testid={`line-approval-badge-${o.id}`}
          className="bg-amber-950/30 border-amber-900/60 text-amber-300 uppercase text-[9px] font-mono">
          Onay Bekliyor {la.pending}/{la.total}
        </Badge>
      )}
      {started && (
        <Badge variant="outline" data-testid={`line-approval-badge-${o.id}`}
          className={`uppercase text-[9px] font-mono ${la.approved === la.total && la.total > 0
            ? "bg-emerald-950/30 border-emerald-900/60 text-emerald-300"
            : "bg-amber-950/30 border-amber-900/60 text-amber-300"}`}>
          {la.approved === la.total && la.total > 0
            ? `Onaylandı ${la.approved}/${la.total}`
            : `Kısmi Onay ${la.approved}/${la.total}`}
        </Badge>
      )}
      {partially && (
        <Badge variant="outline" data-testid={`partial-converted-badge-${o.id}`}
          className="bg-blue-950/30 border-blue-900/60 text-blue-300 uppercase text-[9px] font-mono">
          Kısmen Dönüştürüldü
        </Badge>
      )}
    </>
  );
}

// ---- Quick create dialog ----------------------------------------------------
function QuickOrderDialog({ open, onOpenChange, isQuotes, onSaved }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_id: "",
    product_name: "",
    quantity: "",
    unit_price: "",
    due_date: "",
    expires_at: "",
    priority: "normal",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/customers");
        setCustomers(data || []);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [open]);

  const reset = () => setForm({
    customer_id: "", product_name: "", quantity: "", unit_price: "",
    due_date: "", expires_at: "", priority: "normal", notes: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Müşteri seçin");
    if (!form.product_name.trim()) return toast.error("Ürün adı girin");
    const qty = parseInt(form.quantity, 10);
    if (!(qty > 0)) return toast.error("Adet 0'dan büyük olmalı");

    setSaving(true);
    try {
      await api.post("/orders", {
        customer_id: form.customer_id,
        product_name: form.product_name.trim(),
        quantity: qty,
        unit_price: Number(form.unit_price) || 0,
        due_date: form.due_date || null,
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59").toISOString() : null,
        priority: form.priority,
        notes: form.notes,
      });
      toast.success(isQuotes ? "Teklif oluşturuldu" : "Sipariş oluşturuldu");
      reset();
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-slate-50 flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-400" />
            Hızlı {isQuotes ? "Teklif" : "Sipariş"}
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-1">
            Kısa form — detaylı varyant, beden dağılımı ve ürün reçetesi için Sihirbaz'ı kullanın.
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase text-slate-500 font-mono">Müşteri *</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger data-testid="quick-customer" className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1">
                <SelectValue placeholder="Müşteri seç" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 max-h-72">
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase text-slate-500 font-mono">Ürün Adı *</Label>
            <Input
              data-testid="quick-product"
              value={form.product_name}
              onChange={(e) => setForm({ ...form, product_name: e.target.value })}
              className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1"
              placeholder="Örn: İş Tulumu - Lacivert"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase text-slate-500 font-mono">Adet *</Label>
              <Input
                data-testid="quick-qty"
                type="number" min="1"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1 font-mono"
                placeholder="100"
              />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500 font-mono">Birim Fiyat (₺)</Label>
              <Input
                data-testid="quick-price"
                type="number" min="0" step="0.01"
                value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1 font-mono"
                placeholder="150.00"
              />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500 font-mono">Termin</Label>
              <Input
                data-testid="quick-due"
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500 font-mono">
                {isQuotes ? "Geçerlilik" : "Öncelik"}
              </Label>
              {isQuotes ? (
                <Input
                  data-testid="quick-expires"
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1"
                />
              ) : (
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="dusuk">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="yuksek">Yüksek</SelectItem>
                    <SelectItem value="acil">Acil</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {isQuotes && (
              <div className="col-span-2">
                <Label className="text-xs uppercase text-slate-500 font-mono">Öncelik</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="dusuk">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="yuksek">Yüksek</SelectItem>
                    <SelectItem value="acil">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase text-slate-500 font-mono">Notlar</Label>
            <Textarea
              data-testid="quick-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1"
              placeholder="Özel talimatlar…"
            />
          </div>

          {/* Live total */}
          {(form.quantity && form.unit_price) && (
            <div className="text-right text-sm bg-slate-900 border border-slate-800 rounded-sm px-3 py-2">
              <span className="text-slate-500 text-xs uppercase font-mono mr-2">Toplam</span>
              <span className="text-blue-400 font-mono font-bold">
                ₺{(Number(form.quantity) * Number(form.unit_price)).toLocaleString("tr-TR")}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button" variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-slate-800 bg-slate-900 text-slate-300 hover:bg-slate-800 rounded-sm"
            >
              İptal
            </Button>
            <Button
              data-testid="quick-save-btn"
              type="submit"
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500 rounded-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              {isQuotes ? "Teklifi Kaydet" : "Siparişi Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/** FT faturası tarih düzenleme — yalnız BH'a gönderilmemiş faturalar (kilit backend'de). */
export function InvoiceDatesDialog({ order, onClose, onSaved }) {
  const [invDate, setInvDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) {
      setInvDate(String(order.invoice_date || "").slice(0, 10));
      setDueDate(String(order.due_date || "").slice(0, 10));
    }
  }, [order]);

  if (!order) return null;

  const save = async () => {
    if (invDate && dueDate && dueDate < invDate) {
      return toast.error("Vade tarihi fatura tarihinden önce olamaz.");
    }
    setSaving(true);
    try {
      await api.patch(`/orders/${order.id}/invoice-dates`, {
        ...(invDate ? { invoice_date: invDate } : {}),
        due_date: dueDate,
      });
      toast.success("Fatura tarihleri güncellendi");
      onClose();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-sm" data-testid="ft-dates-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2 text-base">
            <CalendarDays className="w-5 h-5 text-amber-400" />
            Fatura Tarihleri — <span className="font-mono text-amber-300">{order.order_no}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Fatura Tarihi</Label>
            <Input
              type="date"
              data-testid="ft-invoice-date-input"
              value={invDate}
              onChange={(e) => setInvDate(e.target.value)}
              className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Vade Tarihi</Label>
            <Input
              type="date"
              data-testid="ft-due-date-input"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="bg-slate-950 border-slate-700 rounded-sm h-8 text-xs mt-1"
            />
          </div>
          <div className="text-[10px] text-slate-500 font-mono">
            Bizim Hesap gönderimi bu tarihleri kullanır.
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="ft-dates-cancel-btn"
            className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"
          >
            İptal
          </Button>
          <Button
            data-testid="ft-dates-save-btn"
            onClick={save}
            disabled={saving || !invDate}
            className="bg-amber-600 hover:bg-amber-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Teklif sonuçlandırma dialogları (liste + detay sayfası REUSE) ----------
export const QUOTE_CLOSE_REASONS = ["Fiyat", "Rakip Firma", "Termin", "Müşteri Vazgeçti", "Ödeme Şartları", "Proje Ertelendi", "Diğer"];

export function QuoteCloseDialog({ order, stage, onClose, onSaved }) {
  const [closeReason, setCloseReason] = useState("");
  const [closeNote, setCloseNote] = useState("");
  const [closeBusy, setCloseBusy] = useState(false);
  useEffect(() => {
    if (order) { setCloseReason(""); setCloseNote(""); }
  }, [order]);

  const submitClose = async () => {
    if (!closeReason) { toast.error("Sebep seçimi zorunludur"); return; }
    if (closeReason === "Diğer" && !closeNote.trim()) { toast.error("'Diğer' sebebi için açıklama zorunludur"); return; }
    setCloseBusy(true);
    try {
      await api.patch(`/orders/${order.id}/stage`, {
        stage,
        cancel_reason: closeReason,
        cancel_note: closeNote.trim() || null,
      });
      toast.success(stage === "reddedildi" ? "Teklif reddedildi olarak işlendi" : "Teklif iptal edildi");
      onClose();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCloseBusy(false);
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-800" data-testid="quote-close-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50">
            {stage === "reddedildi" ? "Teklifi Reddet" : "Teklifi İptal Et"} — {order?.order_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-slate-500">
            Teklif silinmez; arşive taşınır ve geçmiş kayıt olarak saklanır.
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500 font-mono">Sebep *</Label>
            <Select value={closeReason} onValueChange={setCloseReason}>
              <SelectTrigger data-testid="quote-close-reason" className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm mt-1">
                <SelectValue placeholder="Sebep seçin" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {QUOTE_CLOSE_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500 font-mono">
              Açıklama {closeReason === "Diğer" ? "*" : "(opsiyonel)"}
            </Label>
            <Textarea
              data-testid="quote-close-note"
              rows={2}
              value={closeNote}
              onChange={(e) => setCloseNote(e.target.value)}
              className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm mt-1"
              placeholder={closeReason === "Diğer" ? "Açıklama zorunludur…" : "Ek açıklama…"}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-sm">Vazgeç</Button>
          <Button
            data-testid="quote-close-submit"
            onClick={submitClose}
            disabled={closeBusy || !closeReason || (closeReason === "Diğer" && !closeNote.trim())}
            className={`rounded-sm ${stage === "reddedildi" ? "bg-amber-600 hover:bg-amber-500" : "bg-rose-600 hover:bg-rose-500"}`}
          >
            {closeBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {stage === "reddedildi" ? "Reddedildi Olarak İşaretle" : "İptal Et"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuoteCloseInfoDialog({ order, labels, onClose }) {
  const fmtD = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return "—"; }
  };
  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-800" data-testid="quote-close-info-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50">Teklif Sonuçlanma Detayı — {order?.order_no}</DialogTitle>
        </DialogHeader>
        {order && (
          <div className="space-y-2 text-sm">
            <InfoRow label="Durum" value={labels?.[order.stage] || order.stage} testid="quote-info-status" />
            <InfoRow label="Sebep" value={order.cancel_reason || "Sebep belirtilmemiş"} testid="quote-info-reason" />
            {order.cancel_note && <InfoRow label="Açıklama" value={order.cancel_note} testid="quote-info-note" />}
            <InfoRow label="İşlem Tarihi" value={fmtD(order.cancelled_at || order.updated_at)} testid="quote-info-date" />
            {order.cancelled_by && <InfoRow label="İşlemi Yapan" value={order.cancelled_by} testid="quote-info-by" />}
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-500 rounded-sm">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
